<template>
	<q-page padding class="bg-white">
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-user" class="q-mb-sm" /> {{ $t(page_title) }}
		</div>

		<div class="row gutter-xs">
			<div class="col-xs-12 col-sm-12" v-if="register_others.help_header_slug">
				<q-alert color="primary" icon="fas fa-exclamation-triangle">
					{{ $t(register_others.help_header_slug) }}
				</q-alert>
			</div>

			<div class="col-xs-12 col-sm-12">
				<span class="q-headline text-weight-light">{{ $t('personal_data') }}</span>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="register_fields.name">
				<q-input
					v-model="full_name"
					:float-label="`${$t('name')} ${register_fields.name_mandatory ? '*' : ''}`"
					:error="$v.full_name.$error"
					@input="$v.full_name.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.email_fields">
				<q-field :error="email_error">
					<span slot="error-label" v-if="email_error">
						{{ $t('email_already_in_use_click') }}
						<router-link
							:to="{
								name: 'recover-password',
								params: { email: email ? email : '', document: '' },
							}"
							><strong>{{ $t('here') }}</strong></router-link
						>
						{{ $t('to_recover_your_password') }}
					</span>

					<q-input
						v-model="email"
						type="email"
						:float-label="
							`${$t('email')} ${register_fields.email_fields_mandatory ? '*' : ''}`
						"
						:error="$v.email.$error"
						@input="$v.email.$touch"
						@blur="emailUnique()"
						:disable="disable_email"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.email_fields">
				<q-input
					v-model="email_confirm"
					type="email"
					:float-label="
						`${$t('email_confirm')} ${
							register_fields.email_fields_mandatory ? '*' : ''
						}`
					"
					:error="$v.email_confirm.$error"
					@input="$v.email_confirm.$touch"
					:disable="disable_email"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.document">
				<q-field :error="document_error">
					<span slot="error-label" v-if="document_error">
						{{ $t('document_already_in_use_click') }}
						<router-link
							:to="{
								name: 'recover-password',
								params: { email: '', document: document ? document : '' },
							}"
							><strong>{{ $t('here') }}</strong></router-link
						>
						{{ $t('to_recover_your_password') }}
					</span>

					<q-input
						v-model="document"
						:float-label="
							`${
								clientName === 'academiacoral' || clientName === 'siemens'
									? $t('document_cpf')
									: $t('document')
							} ${register_fields.document_mandatory ? '*' : ''}`
						"
						:error="$v.document.$error"
						@input="$v.document.$touch"
						@blur="documentUnique()"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.register && !isChevrolet">
				<q-field
					:error="register_error"
					:error-label="register_error ? $t('register_not_unique') : ''"
				>
					<q-input
						v-model="register"
						:float-label="
							`${$t('register')} ${register_fields.register_mandatory ? '*' : ''}`
						"
						:error="$v.register.$error"
						@input="$v.register.$touch"
						@blur="registerUnique()"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.register_2 && isChevrolet">
				<q-field
					:error="register_2_error"
					:error-label="register_2_error ? $t('gmin_number_not_unique') : ''"
				>
					<q-input
						v-model="register_2"
						:float-label="
							`${$t('gmin_number')} ${
								register_fields.register_2_mandatory ? '*' : ''
							}`
						"
						:error="$v.register_2.$error"
						@input="$v.register_2.$touch"
						@blur="register2Unique()"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.birthday">
				<q-datetime
					v-model="birthday"
					type="date"
					:float-label="
						`${$t('birthday')} ${register_fields.birthday_mandatory ? '*' : ''}`
					"
					format="DD/MM"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.gender">
				<q-select
					v-model="gender"
					:options="genders"
					:float-label="`${$t('gender')} ${register_fields.gender_mandatory ? '*' : ''}`"
					clearable
				/>
			</div>
		</div>

		<div class="row gutter-xs q-mt-sm">
			<div class="col-xs-12 col-sm-6" v-if="register_fields.is_handicapped">
				<q-select
					v-model="is_handicapped"
					:options="[
						{ value: true, label: $t('yes') },
						{ value: false, label: $t('no') },
					]"
					:float-label="
						`${$t('is_handicapped')} ${
							register_fields.is_handicapped_mandatory ? '*' : ''
						}`
					"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.handicap_description">
				<q-input
					v-model="handicap_description"
					:float-label="
						`${$t('handicap_description')} ${
							register_fields.handicap_description_mandatory ? '*' : ''
						}`
					"
					:error="$v.handicap_description.$error"
					@input="$v.handicap_description.$touch"
					:disable="!is_handicapped"
				/>
			</div>
		</div>

		<div class="row gutter-xs">
			<div class="col-xs-12 col-sm-12 q-mt-lg" v-if="showPhones">
				<span class="q-headline text-weight-light">{{ $t('phone_number') }}</span>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.mobile_phone_fields">
				<q-input
					v-model="phone_mobile.phone"
					:float-label="
						`${$t('mobile')} ${
							register_fields.mobile_phone_fields_mandatory ? '*' : ''
						}`
					"
					:error="$v.phone_mobile.phone.$error"
					@input="$v.phone_mobile.phone.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.whatsapp_phone_fields">
				<q-input
					v-model="phone_whatsapp.phone"
					:float-label="
						`${$t('whatsapp')} ${
							register_fields.whatsapp_phone_fields_mandatory ? '*' : ''
						}`
					"
					:error="$v.phone_whatsapp.phone.$error"
					@input="$v.phone_whatsapp.phone.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.commercial_phone_fields">
				<q-input
					v-model="phone_commercial.phone"
					:float-label="
						`${$t('commercial')} ${
							register_fields.commercial_phone_fields_mandatory ? '*' : ''
						}`
					"
					:error="$v.phone_commercial.phone.$error"
					@input="$v.phone_commercial.phone.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="register_fields.residential_phone_fields">
				<q-input
					v-model="phone_residential.phone"
					:float-label="
						`${$t('residential')} ${
							register_fields.residential_phone_fields_mandatory ? '*' : ''
						}`
					"
					:error="$v.phone_residential.phone.$error"
					@input="$v.phone_residential.phone.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12 q-mt-lg" v-if="register_fields.address_fields">
				<span class="q-headline text-weight-light">{{ $t('address') }}</span>
			</div>

			<div v-if="keyGooogleMaps" class="col-xs-12 col-sm-12">
				<q-field
					:error="show_maps_alert"
					:error-label="
						$t(
							'not_found_check_that_the_search_is_spelled_correctly_Try_adding_a_city_state_or_zip_code'
						)
					"
				>
					<q-input
						v-model="address"
						:float-label="$t('address')"
						autocomplete="new-password"
						:error="$v.address.$error"
						@input="$v.address.$touch"
						:placeholder="$t('search_by_name_and_street_number')"
					>
						<q-autocomplete
							@search="getAddressGoogleMaps"
							:min-characters="3"
							@selected="selectedAddressGoogleMaps"
							separator
						/>
					</q-input>
				</q-field>
			</div>

			<div v-else class="col-xs-12 col-sm-12">
				<div class="col-xs-12 col-sm-12" v-if="register_fields.address_fields">
					<q-input
						v-model="address"
						:float-label="
							`${$t('address')} ${
								register_fields.address_fields_mandatory ? '*' : ''
							}`
						"
						autocomplete="new-password"
						:error="$v.address.$error"
						@input="$v.address.$touch"
					>
						<q-autocomplete
							@search="getAddress"
							:min-characters="3"
							@selected="selectedAddress"
							separator
						/>
					</q-input>
				</div>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input v-model="number" :float-label="$t('number')" ref="number" />
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input v-model="complement" :float-label="$t('complement')" />
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="neighborhood"
					:float-label="
						`${$t('neighborhood')} ${
							register_fields.address_fields_mandatory ? '*' : ''
						}`
					"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="postal_code"
					:float-label="
						`${$t('postal_code')} ${
							register_fields.address_fields_mandatory ? '*' : ''
						}`
					"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="city"
					:float-label="
						`${$t('city')} ${register_fields.address_fields_mandatory ? '*' : ''}`
					"
					:error="$v.city.$error"
					@input="$v.city.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-4" v-if="register_fields.address_fields">
				<q-input
					v-model="state"
					:float-label="
						`${$t('state')} ${register_fields.address_fields_mandatory ? '*' : ''}`
					"
					:error="$v.state.$error"
					@input="$v.state.$touch"
				/>
			</div>
		</div>

		<div
			class="row gutter-xs"
			v-if="clientName !== 'contiacademy' && clientName !== 'universidadeasus'"
		>
			<div class="col-xs-12 col-sm-12 q-mt-lg" v-if="register_fields.store">
				<span class="q-headline text-weight-light">{{ $t('company') }}</span>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="showCountries">
				<q-select
					v-model="country"
					:options="countries"
					:float-label="`${$t('country')} ${register_fields.store_mandatory ? '*' : ''}`"
					:error="$v.country.$error"
					@input="loadStores()"
				/>
			</div>

			<div
				class="col-xs-12 col-sm-12"
				v-if="register_fields.store && !register_others.store_as_list"
			>
				<q-input
					v-model="store_code"
					:float-label="`${$t('company')} ${register_fields.store_mandatory ? '*' : ''}`"
					:error="$v.store_code.$error"
					@input="$v.store_code.$touch"
					:disable="!country"
				>
					<q-autocomplete
						@search="searchStore"
						:min-characters="3"
						@selected="selectedStore"
					/>
				</q-input>
			</div>

			<div
				class="col-xs-12 col-sm-12"
				v-if="register_fields.store && register_others.store_as_list"
			>
				<q-select
					v-model="store_code"
					:options="stores"
					:float-label="`${$t('company')} ${register_fields.store_mandatory ? '*' : ''}`"
					:error="$v.store_code.$error"
					@input="selectStoreList"
					:disable="!country"
					filter
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="show_other_company">
				<q-input
					v-model="other_company"
					:float-label="$t('others')"
					:error="$v.other_company.$error"
					@input="$v.other_company.$touch"
				/>
			</div>
		</div>

		<div
			class="row gutter-xs"
			v-for="(client, index) in selected_store.clients"
			v-if="
				register_fields.store_profile &&
					!show_other_company &&
					clientName !== 'contiacademy' &&
					clientName !== 'universidadeasus'
			"
		>
			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="client.profile"
					:options="getProfileOptionsClient(client)"
					:float-label="`${$t('select_profile')} - ${client.name}`"
					:error="client.has_error"
					@input="client.has_error = false"
				/>
			</div>
		</div>

		<div
			class="row gutter-xs"
			v-if="
				register_fields.store_profile &&
					show_other_company &&
					clientName !== 'contiacademy' &&
					clientName !== 'universidadeasus'
			"
		>
			<div class="col-12">
				<q-select
					v-model="profile"
					:options="getProfileOptions(profiles)"
					:float-label="$t('profile')"
					:error="$v.profile.$error"
					@input="$v.profile.$touch"
				/>
			</div>
		</div>

		<div
			class="row gutter-xs"
			v-if="register_fields.store_profile && !show_other_company && clientName === 'siemens'"
		>
			<div class="col-12">
				<q-select
					v-model="profile"
					:options="getProfileOptions(profiles)"
					:float-label="$t('profile')"
					:error="$v.profile.$error"
					@input="$v.profile.$touch"
				/>
			</div>
		</div>

		<div
			class="row gutter-xs"
			v-if="
				register_fields.store_profile &&
					!show_other_company &&
					clientName === 'academiacoral'
			"
		>
			<div class="col-12">
				<q-select
					v-model="profile"
					:options="getProfileOptionsCoral(profiles)"
					:float-label="$t('profile')"
					:error="$v.profile.$error"
					@input="$v.profile.$touch"
				/>
			</div>
		</div>

		<div class="row gutter-xs" v-if="clientName === 'contiacademy'">
			<div class="col-xs-12 col-sm-12 q-mt-lg">
				<span class="q-headline text-weight-light">{{ $t('company') }}</span>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="country"
					:options="countries"
					:float-label="`${$t('country')} ${register_fields.store_mandatory ? '*' : ''}`"
					:error="$v.country.$error"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="contiacademy_partner"
					:options="[
						{ value: true, label: $t('yes') },
						{ value: false, label: $t('no') },
					]"
					:float-label="$t('are_you_a_continental_partner')"
					@input="changeContiacademyPartner"
					:disable="!country"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="contiacademy_partner">
				<q-field
					:error="sap_code_error"
					:error-label="
						contiacademy_custom_store_error_label
							? $t('wait_load_and_select_store_code')
							: $t('sap_code_error')
					"
				>
					<q-input
						v-model="store_code"
						:float-label="`${$t('sap_code')} *`"
						:error="$v.store_code.$error || selected_store.length > 0"
						@input="$v.store_code.$touch"
						:disable="!country"
					>
						<q-autocomplete
							@search="searchStoreConti"
							:min-characters="3"
							@selected="selectedStore"
						/>
					</q-input>
				</q-field>
			</div>

			<div class="col-12" v-if="contiacademy_partner">
				<div class="row gutter-xs" v-for="(client, index) in selected_store.clients">
					<div class="col-xs-12 col-sm-12" v-show="index === 0">
						<q-select
							v-model="client.profile"
							:options="client.profiles"
							:float-label="`${$t('select_profile')}`"
							:error="client.has_error"
							@input="changeProfilesContiacademy"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="row gutter-xs" v-if="clientName == 'universidadeasus'">
			<div class="col-xs-12 col-sm-12 q-mt-lg">
				<span class="q-headline text-weight-light">{{ $t('company') }}</span>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="asus_partner"
					:options="[
						{ value: true, label: $t('yes') },
						{ value: false, label: $t('no') },
					]"
					:float-label="`${$t('you_are_a_retailer')} *`"
					:error="$v.asus_partner.$error"
					@input="changeAsusPartner"
					:disable="!country"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="asus_partner">
				<q-select
					v-model="store_code"
					:options="stores"
					:float-label="`${$t('company')} ${register_fields.store_mandatory ? '*' : ''}`"
					:error="$v.store_code.$error"
					@input="changeAsusProfile"
					:disable="!country"
					filter
				/>
			</div>

			<div class="col-12" v-if="asus_partner === false">
				<div
					class="row gutter-xs"
					v-for="(client, index) in selected_store.clients"
					v-if="register_fields.store_profile && !show_other_company"
				>
					<div class="col-xs-12 col-sm-12">
						<q-select
							v-model="client.profile"
							:options="client.profiles"
							:float-label="`${$t('select_profile')} - ${client.name}`"
							:error="client.has_error"
							@input="client.has_error = false"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="row gutter-xs justify-end q-mt-lg" v-if="register_others.use_accept_terms">
			<div class="col-xs-12 col-sm-3 text-right">
				<q-checkbox
					v-model="accept_terms_and_conditions"
					:label="$t('accept_terms_and_conditions')"
					@input="showTerms()"
					:class="$v.accept_terms_and_conditions.$error ? 'text-negative' : ''"
				/>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="fas fa-chevron-left"
					@click="$router.push({ name: 'login' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>
		</div>

		<q-dialog v-model="user_pre_created" @ok="closeUserPreCreated()">
			<span slot="title">{{ $t('user_registered') }}</span>

			<span slot="body">
				{{
					$t(
						'your_account_has_been_created_and_is_awaiting_for_aproval_you_will_receive_an_email'
					)
				}}
			</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="positive" :label="$t('ok')" @click="closeUserPreCreated()" />
			</template>
		</q-dialog>

		<q-dialog v-model="user_created" @ok="closeUserCreated()">
			<span slot="title">{{ $t('user_registered') }}</span>

			<span slot="body">
				{{ $t('your_account_has_been_created_verify_your_email_for_instructions') }}
			</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="positive" :label="$t('ok')" @click="closeUserCreated()" />
			</template>
		</q-dialog>

		<q-modal
			v-model="show_terms"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			@hide="show_terms = false"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_terms = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('terms_and_conditions') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row text-weight-regular q-pt-md">
						<div class="col-12">
							<span v-html="getTerm"></span>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-sm">
						<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
							<q-btn
								color="negative"
								class="full-width"
								:label="$t('reject_terms')"
								icon="fas fa-ban"
								@click="rejectTerms()"
							/>
						</div>

						<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
							<q-btn
								color="positive"
								class="full-width"
								:label="$t('accept_terms')"
								icon="far fa-save"
								@click="acceptTerms()"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog prevent-close v-model="show_academiacoral_welcome">
			<span slot="title">{{ $t('user_registered_success') }}</span>

			<div slot="message">
				<p slot="message">
					{{ $t('academiacoral_registered_success') }}
				</p>
				<p slot="message">
					{{ $t('use_document_and_default_password_to_login') }}
				</p>
				<p slot="message">
					{{ $t('password') }}: <strong>{{ academiacoral_default_password }}</strong>
				</p>
			</div>

			<template slot="buttons" slot-scope="props">
				<q-btn
					color="positive"
					icon="fas fa-sign-in-alt"
					:label="$t('access_platform')"
					@click="academiaCoralAutoLogin"
				/>
			</template>
		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
import { required, requiredIf, email, sameAs } from 'vuelidate/lib/validators'
import firebase from 'firebase/app'
import 'firebase/app'
import 'firebase/auth'
import { OpenStreetMapProvider } from 'leaflet-geosearch'
const provider = new OpenStreetMapProvider({
	params: {
		addressdetails: '1',
	},
})
const { formatDate } = date

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'login', 'users'] },

	data() {
		return {
			academiacoral_default_password: '',
			accept_terms_and_conditions: false,
			address: '',
			address_id: '',
			asus_partner: '',
			birthday: null,
			city: '',
			complement: '',
			contiacademy_custom_store_error_label: false,
			contiacademy_partner: true,
			countries: [],
			country: '',
			disable_email: false,
			document: '',
			document_error: false,
			email: '',
			email_confirm: '',
			email_confirm_error: false,
			email_error: false,
			error_other_company: false,
			full_name: '',
			gender: null,
			genders: [],
			handicap_description: '',
			is_handicapped: false,
			lat: '',
			long: '',
			neighborhood: '',
			number: '',
			option_address: [],
			origin_register: '',
			other_company: '',
			page_title: 'user_register',
			phone_types: [],
			postal_code: '',
			profile: '',
			profiles: [],
			register: '',
			register_2: '',
			register_2_error: false,
			register_error: false,
			sap_code_error: false,
			selected_store: [],
			send_welcome_mail: true,
			show_academiacoral_welcome: false,
			show_maps_alert: false,
			show_other_company: false,
			show_terms: false,
			showCountries: true,
			state: '',
			store_code: '',
			stores: [],
			terms: [],
			user_created: false,
			user_pre_created: false,
			webservice_login: false,
			phone_commercial: {},
			phone_mobile: {},
			phone_residential: {},
			phone_whatsapp: {},
		}
	},

	computed: {
		...mapGetters({
			login_user_pre_register: 'params/login_user_pre_register',
			login_user_register: 'params/login_user_register',
			register_fields: 'params/register_fields',
			register_others: 'params/register',
			languages: 'params/languages',
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_language_selected: 'common/id_user_language_selected',
			login_default_language_id: 'params/login_default_language_id',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
				},
			})
		},

		getAuthHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		addressRequired() {
			if (this.address || this.city || this.state) return true
			else return false
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		getTerm() {
			let obj = this.terms.find((obj) => {
				return obj.language === this.id_user_language_selected && obj.body !== null
			})
			if (obj) {
				return obj.body
			} else {
				let obj = this.terms.find((obj) => {
					return obj.body !== ''
				})
				if (obj) {
					return obj.body
				}
			}
			return ''
		},

		clientName() {
			return process.env.PROJECT_FOLDER
		},

		keyGooogleMaps() {
			return process.env.KEY_GOOGLE_API
		},

		isChevrolet() {
			return process.env.PROJECT_FOLDER === 'universidadechevrolet' ? true : false
		},

		showPhones() {
			if (
				this.register_fields.mobile_phone_fields ||
				this.register_fields.whatsapp_phone_fields ||
				this.register_fields.residential_phone_fields ||
				this.register_fields.commercial_phone_fields
			)
				return true
			else return false
		},
	},

	methods: {
		fetchData() {
			this.$axios
				.get('/etraining_login/terms/', this.getHeader)
				.then((response) => {
					this.terms = response.data
				})
				.catch((e) => {
					console.log(e)
				})
		},

		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get(`/register_user/get_register_lists/`, this.getHeader)
				.then((response) => {
					this.genders = response.data.genders.map((e) => {
						return { ...e, value: e.id, label: this.$t(e.slug) }
					})

					this.phone_types = response.data.phone_types.map((e) => {
						return {
							...e,
							value: e.id,
							label: this.$t(e.slug),
							field: `${e.slug}_phone_fields`,
						}
					})

					this.countries = response.data.countries.map((e) => {
						return { ...e, value: e.id, label: e.name }
					})

					this.profiles = response.data.profiles.map((e) => {
						return { ...e, label: e.profile, value: e.id }
					})

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
				.finally((e) => {
					this.checkIfSingleCountry()
				})
		},

		saveData() {
			let has_errors = false

			if (this.clientName === 'contiacademy' && this.contiacademy_partner) {
				if (Object.keys(this.selected_store).length === 0) {
					this.store_code = ''
					this.contiacademy_custom_store_error_label = true
				}
			}

			this.$v.$touch()

			if (this.show_other_company) {
				if (!this.other_company.trim()) {
					this.$q.notify({
						message: this.$t('make_sure_that_all_fields_are_filled_in'),
						type: 'negative',
						icon: 'fas fa-exclamation-triangle',
						position: 'top',
					})
					return
				}
			}

			if (
				this.register_fields.store &&
				!this.show_other_company &&
				this.clientName !== 'siemens'
			) {
				if (this.selected_store.clients) {
					this.selected_store.clients.forEach((client) => {
						client.has_error = false

						if (!client.profile) {
							if (this.register_others.use_default_profile) {
								has_errors = false
								client.has_error = false
							} else {
								has_errors = true
								client.has_error = true
							}
						} else client.error_profile = false
					})
				} else {
					if (this.register_fields.store && !this.register_fields.store_mandatory)
						has_errors = false
					else has_errors = true
				}
			}

			let language = this.languages.find((l) => {
				return l.code.split('-')[0] === this.$i18n.i18next.language
			})

			if (!this.$v.$error && !has_errors) {
				this.$q.loading.show()

				let user_clients = []
				let phones = []
				let user_data = []

				let addresses = [
					{
						address: this.address,
						city: this.city,
						complement: this.complement,
						lat: this.lat,
						long: this.long,
						neighborhood: this.neighborhood,
						number: this.number,
						postal_code: this.postal_code,
						state: this.state,
						country: this.country,
					},
				]

				if (
					this.register_fields.store &&
					!this.show_other_company &&
					this.clientName !== 'siemens'
				) {
					if (this.selected_store.clients) {
						this.selected_store.clients.forEach((user_client) => {
							user_clients.push({
								client: user_client.id,
								profile: !this.register_others.use_default_profile
									? user_client.profile
									: this.register_others.default_profile_id,
								country: this.country,
							})
						})
					} else if (this.register_fields.store && !this.selected_store.clients) {
						user_clients.push({
							profile: this.register_others.use_default_profile
								? this.register_others.default_profile_id
								: this.profile,
							country: this.country,
						})
					}
				} else {
					user_clients.push({
						profile: this.register_others.use_default_profile
							? this.register_others.default_profile_id
							: this.profile,
						country: this.country,
					})
				}

				if (this.phone_mobile.phone) {
					phones.push({
						...this.phone_mobile,
						phone_type: 3,
						area_code: '',
						country_code: '',
					})
				}
				if (this.phone_residential.phone) {
					phones.push({
						...this.phone_residential,
						phone_type: 2,
						area_code: '',
						country_code: '',
					})
				}
				if (this.phone_commercial.phone) {
					phones.push({
						...this.phone_commercial,
						phone_type: 1,
						area_code: '',
						country_code: '',
					})
				}
				if (this.phone_whatsapp.phone) {
					phones.push({
						...this.phone_whatsapp,
						phone_type: 4,
						area_code: '',
						country_code: '',
					})
				}

				user_data = {
					addresses: addresses,
					birthday: date.formatDate(this.birthday, 'YYYY-MM-DD'),
					company_branches: this.selected_store.id ? this.selected_store.id : null,
					document: this.document,
					gender: this.gender,
					handicap_description: this.handicap_description,
					is_handicapped: this.is_handicapped,
					phones: phones,
					register: this.register,
					register_2: this.register_2,
				}

				if (this.show_other_company) {
					user_data.company_branch_name = this.other_company
				}

				let data = {
					email: this.email,
					full_name: this.full_name,
					user_data: user_data,
					user_clients: user_clients,
					language: language ? language.id : this.login_default_language_id,
					accept_terms_and_conditions: this.accept_terms_and_conditions,
					send_welcome_mail: this.send_welcome_mail,
					webservice_login: this.webservice_login,
				}

				if (this.clientName == 'universidadeasus') {
					let typeEmail = /@acbz.com.br/g
					let typeEmailDois = /@asus.com/g

					let result_teste_email =
						!typeEmail.test(data.email) && !typeEmailDois.test(data.email)

					if (data.user_clients[0].profile == 3) {
						if (result_teste_email) {
							this.$q.notify({
								message: this.$t('invalid_email_for_this_profile'),
								type: 'negative',
								icon: 'fas fa-exclamation-triangle',
								position: 'top',
							})
							this.$q.loading.hide()
							return
						}
					}

					this.$axios
						.post(`/register_user/register_user_asus/`, data, this.getHeader)
						.then((response) => {
							if (
								typeEmail.test(data.email) ||
								typeEmailDois.test(data.email) ||
								data.user_clients[0].profile == 8 ||
								data.user_clients[0].profile == 4
							) {
								this.user_created = true
							} else {
								this.user_pre_created = true
							}
							this.$q.loading.hide()
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else if (this.clientName === 'academiacoral') {
					this.$axios
						.post(`/register_user/register_user_coral/`, data, this.getHeader)
						.then((response) => {
							this.academiacoral_default_password = response.data.default_password
							this.show_academiacoral_welcome = true

							this.$q.loading.hide()
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else {
					if (this.login_user_register) {
						this.$axios
							.post(`/register_user/register_user/`, data, this.getHeader)
							.then(() => {
								if (this.send_welcome_mail) {
									this.user_created = true

									this.$q.loading.hide()
								}
							})
							.catch((e) => {
								this.$q.loading.hide()
								console.log(e)
							})
					} else if (this.login_user_pre_register) {
						this.$axios
							.post('/register_user/pre_register_user/', data, this.getHeader)
							.then(() => {
								this.user_pre_created = true
								this.$q.loading.hide()
							})
							.catch((e) => {
								this.$q.loading.hide()
								console.log(e)
							})
					} else {
						this.$q.loading.hide()
					}
				}
			} else {
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		setUserData(response) {
			this.$store.dispatch('common/SET_ACCESS_TOKEN', [
				response.data.access_token,
				response.data.refresh_token,
				response.data.user_firebase_token,
			])

			if (response.data.user_firebase_token) {
				firebase
					.auth()
					.signInWithCustomToken(response.data.user_firebase_token)
					.then(() => {
						if (this.$q.platform.is.cordova) {
							this.setUserDeviceToken()
						} else {
							this.getWelcomeBanner()
						}
					})
					.catch(function(error) {
						var errorCode = error.code
						var errorMessage = error.message
					})
			} else {
				if (this.$q.platform.is.cordova) {
					this.setUserDeviceToken()
				} else {
					this.getWelcomeBanner()
				}
			}
		},

		setUserDeviceToken() {
			// window.plugins.OneSignal.getIds((ids) => {
			//     let fd = new FormData()
			//     fd.append('token', ids.userId)
			//     fd.append('device_type', this.$q.platform.is.android ? 'android' : 'ios')

			//     this.$axios.post('/etraining_login/set_user_device_token', fd, this.getAuthHeader)
			//     .then(response => {
			this.getWelcomeBanner()
			//     })
			//     .catch(e => {
			//         this.getWelcomeBanner()
			//         console.log(e)
			//     })
			// })
		},

		getWelcomeBanner() {
			this.$axios
				.get('/etraining_banners_welcome/banners_welcome/', this.getAuthHeader)
				.then((response) => {
					this.$store.dispatch('welcome_banner/SET_WELCOME_BANNER', [response.data])
					this.getUserData()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		getUserData() {
			this.$axios
				.get('/accounts/read_user_data/', this.getAuthHeader)
				.then((response) => {
					const d = response.data

					this.$store.dispatch('common/SET_USER_DATA', [
						d.id,
						d.id_user_client_profile,
						d.full_name,
						d.document,
						d.id_user_client_selected,
						d.id_user_client_country_selected,
						d.id_user_language_selected,
						d.user_clients,
						d.user_client_module_items,
						d.user_firebase_document,
						d.is_superuser,
						d.email,
						d.user_firebase_document_ranking_areas,
						d.has_mandatory_surveys,
						d.accept_terms_and_conditions,
						d.user_companies,
						d.user_branches,
					])

					const obj = this.clients.find(
						(obj) => obj.id === response.data.id_user_client_selected
					)
					if (obj) {
						if (obj.home_route) {
							this.$router.replace({ name: obj.home_route })
						} else {
							if (obj.has_courses) {
								this.$router.replace({ name: 'courses' })
							} else if (obj.has_trails) {
								this.$router.replace({ name: 'trails' })
							} else if (obj.has_library) {
								this.$router.replace({ name: 'library' })
							}
						}
					} else {
						this.$router.replace({ name: 'courses' })
					}
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		getAddress(terms, done) {
			provider.search({ query: terms }).then((result) => {
				done(result)
			})
		},

		selectedAddress(item) {
			this.$nextTick(() => (this.address = item.raw.address.road))

			this.postal_code = item.raw.address.postcode
			this.city = item.raw.address.city
			this.state = item.raw.address.state
			this.lat = item.raw.lat
			this.long = item.raw.lon
			// this.country = item.raw.address.country

			if (item.raw.address.neighbourhood) {
				this.neighborhood = item.raw.address.neighbourhood
			} else if (item.raw.address.suburb) {
				this.neighborhood = item.raw.address.suburb
			} else if (item.raw.address.city_district) {
				this.neighborhood = item.raw.address.city_district
			}

			this.$nextTick(() => this.$refs.number.$el.focus())
		},

		getAddressGoogleMaps(terms, done) {
			this.$axios
				.get(
					`https://maps.googleapis.com/maps/api/geocode/json?address=${terms}&key=${
						this.keyGooogleMaps
					}`
				)
				.then((response) => {
					if (response.data.results.length > 0) {
						this.option_address = response.data.results
						let teste = response.data.results.map((e) => ({
							label: e.formatted_address,
							value: e.formatted_address,
							place_id: e.place_id,
						}))
						done(teste)
					} else {
						this.show_maps_alert = true
					}
				})
				.catch((e) => {
					console.log(e)
				})
		},

		selectedAddressGoogleMaps(item) {
			this.show_maps_alert = false

			this.address = ''
			this.postal_code = ''
			this.city = ''
			this.state = ''
			this.lat = ''
			this.long = ''
			this.neighborhood = ''
			this.number = ''

			this.option_address.map((x) => {
				if (x.place_id === item.place_id) {
					this.lat = x.geometry.location.lat
					this.long = x.geometry.location.lng
					x.address_components.map((e) => {
						switch (e.types[0]) {
							case 'route':
								this.address = e.long_name
								break
							case 'political':
							case 'administrative_area_level_4':
								this.neighborhood = e.long_name
								break
							case 'street_number':
								this.number = e.long_name
								break
							case 'postal_code':
								this.postal_code = e.long_name
								break
							case 'administrative_area_level_2':
								this.city = e.long_name
								break
							case 'administrative_area_level_1':
								this.state = e.long_name
								break
						}
					})
				}
			})
		},

		searchStore(terms, done) {
			let fd = new FormData()
			fd.append('search', terms)
			fd.append('country', this.country)

			this.$axios
				.post(`/register_user/search_branch/`, fd, this.getHeader)
				.then((response) => {
					let stores = []

					response.data.forEach((element) => {
						stores.push({
							value: `${element.name} ${element.document ? '- ' : ''} ${
								element.document
							} ${element.register ? ' - ' : ''} ${element.register}`,
							label: `${element.name} ${element.document ? '- ' : ''} ${
								element.document
							} ${element.register ? ' - ' : ''} ${element.register}`,
							address: element.address,
							clients: element.clients,
							id: element.id,
							name: element.name,
						})
					})

					done(stores)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		searchStoreConti(terms, done) {
			let fd = new FormData()
			fd.append('search', terms)
			fd.append('country', this.country)
			fd.append('client', 'contiacademy')
			this.$q.loading.show()

			this.$axios
				.post(`/register_user/search_branch/`, fd, this.getHeader)
				.then((response) => {
					let stores = []

					if (response.data.length === 0) this.sap_code_error = true
					else this.sap_code_error = false

					response.data.forEach((element) => {
						stores.push({
							value: `${element.name} ${element.register ? ' - ' : ''} ${
								element.register
							}`,
							label: `${element.name} ${element.register ? ' - ' : ''} ${
								element.register
							}`,
							sublabel: `${element.document ? this.$t('document') : ''} ${
								element.document
							} `,
							address: element.address,
							clients: element.clients,
							id: element.id,
							name: element.name,
						})
					})

					this.$q.loading.hide()

					done(stores)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		loadStores() {
			this.$v.country.$touch
			if (this.register_others.store_as_list) {
				this.$q.loading.show()

				let fd = new FormData()
				fd.append('country', this.country)

				this.$axios
					.post(`/register_user/new_list_branches/`, fd, this.getHeader)
					.then((response) => {
						this.stores = []

						response.data.forEach((element) => {
							this.stores.push({
								value: element.id,
								label: `${element.name}`,
								address: element.address,
								clients: element.clients,
								id: element.id,
								name: element.name,
							})
						})

						this.$q.loading.hide()
					})
					.catch((e) => {
						this.$q.loading.hide()
						console.log(e)
					})
			}
		},

		selectedStore(item) {
			this.selected_store = item
		},

		selectStoreList(id) {
			if (id === -1) {
				this.show_other_company = true
				this.selected_store.id = -1
			} else {
				let obj = this.stores.find((obj) => {
					return obj.value === id
				})
				if (obj) {
					this.selected_store = obj
					this.show_other_company = false
				}
			}
		},

		documentUnique() {
			if (this.document) {
				let fd = new FormData()
				fd.append('document', this.document)

				this.$axios
					.post(`/register_user/check_user_document_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) this.document_error = true
						else this.document_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.document_error = false
		},

		registerUnique() {
			if (this.register) {
				let fd = new FormData()
				fd.append('register', this.register)

				this.$axios
					.post(`/register_user/check_user_register_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) this.register_error = true
						else this.register_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.register_error = false
		},

		register2Unique() {
			if (this.register_2) {
				let fd = new FormData()
				fd.append('register_2', this.register_2)

				this.$axios
					.post(`/register_user/check_user_register_2_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) this.register_2_error = true
						else this.register_2_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.register_2_error = false
		},

		emailUnique() {
			if (this.email) {
				let fd = new FormData()
				fd.append('email', this.email)

				this.$axios
					.post(`/register_user/check_user_email_existance/`, fd, this.getHeader)
					.then((response) => {
						if (response.data.exists) this.email_error = true
						else this.email_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.email_error = false
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		closeUserPreCreated() {
			this.user_pre_created = false
			this.$router.replace({ name: 'login' })
		},

		closeUserCreated() {
			this.user_created = false
			this.$router.replace({ name: 'login' })
		},

		showTerms() {
			if (this.accept_terms_and_conditions) {
				this.accept_terms_and_conditions = false
				this.show_terms = true
			}
		},

		rejectTerms() {
			this.accept_terms_and_conditions = false
			this.show_terms = false
		},

		acceptTerms() {
			this.accept_terms_and_conditions = true
			this.show_terms = false
		},

		showPhone(type) {
			switch (type) {
				case 'mobile':
					return this.register_fields.mobile_phone_fields
					break
				case 'commercial':
					return this.register_fields.commercial_phone_fields
					break
				case 'residential':
					return this.register_fields.residential_phone_fields
					break
				default:
					return false
					break
			}
		},

		changeProfilesContiacademy(value) {
			let profile_label = ''

			this.selected_store.clients.forEach((client) => {
				let tmp = null
				tmp = client.profiles.find((obj) => {
					return obj.value == value
				})
				if (tmp) profile_label = tmp.label
			})

			this.selected_store.clients.forEach((client) => {
				let profile_code = client.profiles.find((obj) => {
					return obj.label == profile_label
				})
				if (profile_code) client.profile = profile_code.value
			})
		},

		changeContiacademyPartner() {
			if (!this.contiacademy_partner) {
				this.store_code = this.register_others.external_user_branch

				let fd = new FormData()
				fd.append('search', this.register_others.external_user_branch)
				fd.append('country', this.country)
				fd.append('client', 'contiacademy')

				this.$axios
					.post(`/register_user/search_branch/`, fd, this.getHeader)
					.then((response) => {
						let stores = []

						let store = response.data[0]

						this.selected_store = {
							value: `${store.name} ${store.register ? ' - ' : ''} ${store.register}`,
							label: `${store.name} ${store.register ? ' - ' : ''} ${store.register}`,
							address: store.address,
							clients: store.clients,
							id: store.id,
							name: store.name,
						}

						this.changeProfilesContiacademy(this.register_others.external_user_profile)
					})
					.catch((e) => {
						console.log(e)
					})
			} else {
				this.store_code = ''
				this.selected_store = {}
			}
		},

		changeAsusPartner() {
			if (this.register_others.store_as_list) {
				this.$q.loading.show()

				let fd = new FormData()
				fd.append('country', this.country)

				this.$axios
					.post(`/register_user/new_list_branches/`, fd, this.getHeader)
					.then((response) => {
						this.stores = []

						this.stores.push({
							value: -1,
							label: this.$t('others'),
							address: {},
							clients: {},
							id: 'other',
							name: this.$t('others'),
						})

						response.data.forEach((element) => {
							this.stores.push({
								value: element.id,
								label: `${element.name}`,
								address: element.address,
								clients: element.clients,
								id: element.id,
								name: element.name,
							})
						})

						this.store_code = ''
						if (!this.asus_partner) {
							this.store_code = 35
							let obj = this.stores.find((obj) => {
								return obj.value === 35
							})
							if (obj) {
								this.selected_store = obj
								this.show_other_company = false
							}
							let position = ''
							this.selected_store.clients[0].profiles.map((p, index) =>
								p.value == 4 ? (position = index) : ''
							)
							this.selected_store.clients[0].profiles.splice(position, 1)
						}

						this.$q.loading.hide()
					})
					.catch((e) => {
						this.$q.loading.hide()
						console.log(e)
					})
			}
		},

		changeAsusProfile() {
			this.selectStoreList(this.store_code)
			this.selected_store.clients[0].profile = 4
		},

		getProfileOptionsClient(client) {
			return client.profiles.map((e) => {
				return {
					value: e.value,
					label: e.translations
						? e.translations.find(
								(l) => l.language_id === this.id_user_language_selected
						  ).name
						: e.label,
				}
			})
		},

		getProfileOptions(profiles) {
			return profiles.map((e) => {
				return {
					value: e.value,
					label: e.translations
						? e.translations.find(
								(l) => l.language_id === this.id_user_language_selected
						  ).name
						: e.label,
				}
			})
		},

		getProfileOptionsCoral(profiles) {
			return profiles
				.map((e) => {
					return {
						label: e.name,
						value: e.value,
					}
				})
				.sort((a, b) => a.label.localeCompare(b.label))
		},

		academiaCoralAutoLogin() {
			this.show_academiacoral_welcome = false
			this.$q.loading.show()

			let fd = new FormData()
			fd.append('grant_type', 'password')
			fd.append('username', this.document)
			fd.append('password', this.academiacoral_default_password)

			this.$axios
				.post('/o/token/', fd, this.getHeader)
				.then((response) => {
					this.$store.dispatch('params/SET_ATTEMPTS', [0])
					this.setUserData(response)
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		checkIfSingleCountry() {
			if (this.countries.length === 1) {
				this.country = this.countries[0].id
				this.showCountries = false
			}

			if (this.clientName === 'academiapoptrade') {
				this.country = this.countries[4].id
				this.showCountries = false
			}
		},
	},

	validations() {
		return {
			full_name: { required },
			email: {
				requiredIf: requiredIf(() => {
					return (
						this.register_fields.email_fields &&
						this.register_fields.email_fields_mandatory
					)
				}),
				email,
				emailUnique() {
					if (!this.email_error) return true
					else return false
				},
			},
			email_confirm: {
				requiredIf: requiredIf(() => {
					return (
						this.register_fields.email_fields &&
						this.register_fields.email_fields_mandatory
					)
				}),
				email,
				sameAsEmail: sameAs('email'),
			},
			document: {
				documentUnique() {
					if (!this.document_error) return true
					else return false
				},
				requiredIf: requiredIf(() => {
					return this.register_fields.document && this.register_fields.document_mandatory
				}),
			},
			register: {
				registerUnique() {
					if (!this.register_error) return true
					else return false
				},
				requiredIf: requiredIf(() => {
					return this.register_fields.register && this.register_fields.register_mandatory
				}),
			},
			address: {
				requiredIf: requiredIf(() => {
					return this.addressRequired && this.register_fields.address_fields_mandatory
				}),
			},
			city: {
				requiredIf: requiredIf(() => {
					return this.addressRequired && this.register_fields.address_fields_mandatory
				}),
			},
			state: {
				requiredIf: requiredIf(() => {
					return this.addressRequired && this.register_fields.address_fields_mandatory
				}),
			},
			store_code: {
				requiredIf: requiredIf(() => {
					if (this.clientName === 'contiacademy') {
						if (this.contiacademy_partner == true) return true
						else return false
					} else {
						return this.register_fields.store && this.register_fields.store_mandatory
					}
				}),
			},
			country: {
				requiredIf: requiredIf(() => {
					return this.register_fields.store && this.register_fields.store_mandatory
				}),
			},
			accept_terms_and_conditions: {
				requiredIf: requiredIf(() => {
					return this.register_others.use_accept_terms
				}),
				sameAsTermsl: sameAs(() => (this.register_others.use_accept_terms ? true : false)),
			},
			other_company: {
				requiredIf: requiredIf(() => {
					return this.show_other_company
				}),
			},
			profile: {
				requiredIf: requiredIf(() => {
					if (
						this.register_fields.store_profile_mandatory &&
						this.clientName !== 'academiacoral'
					) {
						return this.show_other_company
					} else if (
						this.clientName === 'academiacoral' &&
						this.register_fields.store_profile
					) {
						return true
					} else return false
				}),
			},
			asus_partner: {
				requiredIf: requiredIf(() => {
					if (this.clientName == 'universidadeasus') {
						if (this.asus_partner) return false
						else return true
					} else {
						return false
					}
				}),
			},
			is_handicapped: {
				requiredIf: requiredIf(() => {
					return this.register_fields.is_handicapped_mandatory
				}),
			},
			handicap_description: {
				requiredIf: requiredIf(() => {
					return (
						this.register_fields.handicap_description_mandatory && this.is_handicapped
					)
				}),
			},
			register_2: {
				register2Unique() {
					if (!this.register_2_error) return true
					else return false
				},
				requiredIf: requiredIf(() => {
					return this.register_fields.register_2_mandatory && this.isChevrolet
				}),
			},
			phone_mobile: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.mobile_phone_fields_mandatory
					}),
				},
			},
			phone_residential: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.residential_phone_fields_mandatory
					}),
				},
			},
			phone_commercial: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.commercial_phone_fields_mandatory
					}),
				},
			},
			phone_whatsapp: {
				phone: {
					requiredIf: requiredIf(() => {
						return this.register_fields.whatsapp_phone_fields_mandatory
					}),
				},
			},
		}
	},

	created() {
		this.fetchLists()
		this.fetchData()
	},
}
</script>

<style lang="stylus" scoped>
a
    text-decoration none
    color inherit
</style>
